import { useAppTheme } from '@capsicum/common';
import { type MaskStylesObj } from '@reactour/mask';
import { type PopoverStylesObj } from '@reactour/popover';
import { type StylesObj } from '@reactour/tour';
import { useCallback } from 'react';

import { TOPBAR_ZINDEX } from '../../ui/atoms/top-bar/TopBar';

export const TUTORIAL_MASK_WRAPPER_ZINDEX = TOPBAR_ZINDEX + 1;
export const TUTORIAL_POPOVER_ZINDEX = TUTORIAL_MASK_WRAPPER_ZINDEX + 1;

// Arrow CSS settings

type HighlightedAreaStyleProps = {
	disableInteraction?: boolean;
};

export const useTutorialStyles = () => {
	const { theme } = useAppTheme();

	const badgeStyle = useCallback(
		(): StylesObj['badge'] => base => ({
			...base,
			backgroundColor: 'unset',
			boxShadow: 'none',
			color: theme.colors.text.main.subtle,
			left: '8%',
			padding: 0,
			position: 'absolute',
			top: '85%',
			transform: 'translate(-50%, -50%)'
		}),
		[theme.colors.text.main.subtle]
	);

	const controlsStyle = useCallback(
		(): StylesObj['controls'] => base => ({
			...base,
			justifyContent: 'flex-end'
		}),
		[]
	);

	const popoverStyles = useCallback(
		(extend?: React.CSSProperties): PopoverStylesObj['popover'] =>
			base => ({
				...base,
				background: theme.colors.backgrounds.main.screen,
				borderRadius: theme.border.radius.medium['border-radius'],
				fontFamily: theme.fonts.text.text6['font-family'],
				margin: theme.spacings.xsmall,
				maxWidth: '345px',
				padding: theme.spacings.small,
				// to make a smoother popover appearance animation
				transition: 'transform 0.3s ease-in',
				zIndex: TUTORIAL_POPOVER_ZINDEX,
				...extend
			}),
		[theme.border.radius.medium, theme.colors.backgrounds.main.screen, theme.fonts.text.text6, theme.spacings.small]
	);

	const highlightedAreaStyle = useCallback(
		(props?: HighlightedAreaStyleProps): MaskStylesObj['highlightedArea'] =>
			base => ({
				...base,
				display: 'block',
				height: base?.height + 5,
				pointerEvents: props?.disableInteraction ? 'auto' : 'none',
				rx: 8,
				stroke: theme.colors.borders.success.enabled,
				strokeDasharray: 5,
				strokeWidth: 2,
				width: base?.width + 5,
				x: base?.x - 3,
				y: base?.y - 3
			}),
		[theme.colors.borders.success.enabled]
	);

	const clickAreaStyle = useCallback(
		(): MaskStylesObj['clickArea'] => base => ({
			...base,
			rx: 8
		}),
		[]
	);

	const maskWrapperStyle = useCallback(
		(): MaskStylesObj['maskWrapper'] => base => ({
			...base,
			color: theme.colors.common.scrim.background,
			zIndex: TUTORIAL_MASK_WRAPPER_ZINDEX
		}),
		[theme.colors.common.scrim.background]
	);

	const maskAreaStyle = useCallback(
		(): MaskStylesObj['maskArea'] => base => ({
			...base,
			rx: 8
		}),
		[]
	);

	return {
		badgeStyle,
		clickAreaStyle,
		controlsStyle,
		highlightedAreaStyle,
		maskAreaStyle,
		maskWrapperStyle,
		popoverStyles
	};
};
